import type { ISpotlight } from 'src/types/story';

import { useState, useEffect } from 'react';

import { Box, Typography } from '@mui/material';

import { useSpotlightRectangle } from 'src/hooks/use-spotlight-rectangle';

import { MoveableRectangle } from './moveable-rectangle';

interface SpotlightCanvasProps {
  imageUrl: string | null;
  spotlights: ISpotlight[];
  currentPage: number;
  disabled?: boolean;
  onSpotlightChange?: (index: number, rectangle: { x1: number; y1: number; x2: number; y2: number }) => void;
  onSpotlightSelect?: (index: number) => void;
}

export function SpotlightCanvas({
  imageUrl,
  spotlights,
  currentPage,
  disabled,
  onSpotlightChange,
  onSpotlightSelect,
}: SpotlightCanvasProps) {
  const [imageError, setImageError] = useState(false);
  
  const {
    imageRef,
    isDragging,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  } = useSpotlightRectangle({
    initialRectangle: spotlights[currentPage] ? {
      x1: spotlights[currentPage].x1,
      y1: spotlights[currentPage].y1,
      x2: spotlights[currentPage].x2,
      y2: spotlights[currentPage].y2,
    } : undefined,
    onRectangleChange: (rectangle) => onSpotlightChange?.(currentPage, rectangle),
    disabled,
  });

  // Reset image error when imageUrl changes
  useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  if (!imageUrl) return null;

  return (
    <Box sx={{ 
      position: 'relative', 
      width: '100%',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      overflow: 'hidden',
      backgroundColor: 'background.paper',
      boxShadow: (theme) => theme.shadows[1],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <Box
        ref={imageRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchStart={(e) => {
          e.preventDefault();
          const touch = e.touches[0];
          const mouseEvent = new MouseEvent('mousedown', {
            clientX: touch.clientX,
            clientY: touch.clientY,
          });
          handleMouseDown(mouseEvent as any);
        }}
        onTouchMove={(e) => {
          e.preventDefault();
          const touch = e.touches[0];
          const mouseEvent = new MouseEvent('mousemove', {
            clientX: touch.clientX,
            clientY: touch.clientY,
          });
          handleMouseMove(mouseEvent as any);
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          handleMouseUp();
        }}
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          pointerEvents: 'auto',
          touchAction: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {!imageError ? (
          <img
            src={imageUrl}
            alt="Spotlight"
            style={{ 
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
            draggable={false}
            onError={() => setImageError(true)}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              minHeight: 400,
              color: 'text.secondary',
              backgroundColor: 'grey.100',
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Image is processing
            </Typography>
          </Box>
        )}
      </Box>
      {spotlights.map((spotlight: ISpotlight, index: number) => (
        <MoveableRectangle
          key={index}
          index={index}
          spotlight={spotlight}
          onClick={() => onSpotlightSelect?.(index)}
          selected={index === currentPage}
          disabled={disabled ?? false}
          isDragging={isDragging}
          handleMouseDown={handleMouseDown}
          handleMouseMove={handleMouseMove}
          handleMouseUp={handleMouseUp}
        />
      ))}
    </Box>
  );
} 